import { useState } from "react";
import { Box, Paper, Stack, useTheme } from "@mui/material";
import { Banner, CountTabs, FoodsIconButton, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useGetAllScoreType } from "src/api/client-api/scoreType/useGetAllScoreType";
import { useGetAllScore } from "src/api/sure-api/score/useGetAllScore";
import { Loading } from "src/components/utils/Loading";
import { MODIFY, SCORE_TYPE, useFoodpilotContext } from "src/context/FoodpilotContext.tsx";
import { useNavigateSettingsParent } from "../useNavigateSettingsParent";
import { EntityScoresSettings } from "./components/EntityScoresSettings";
import {
    getDisplayedScores,
    getScoreTypeSelection,
    SCORES_DISPLAYED_ENTITY_TYPES,
    ScoresDisplayedEntityType,
} from "./utils/scoreTypeSettings";

export const ScoresDisplayed = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { canUser } = useFoodpilotContext();
    const navigateSettingsParent = useNavigateSettingsParent();
    const { scores } = useGetAllScore();
    const { scoreTypes } = useGetAllScoreType();
    const [activeType, setActiveType] = useState<ScoresDisplayedEntityType>(SCORES_DISPLAYED_ENTITY_TYPES[0]);

    if (scores === undefined || scoreTypes === undefined) return <Loading />;

    const tabLabels: Record<ScoresDisplayedEntityType, string> = {
        company: t("settings.scores.tabs.organization", "Organization"),
        product: t("sidebar.products.drawer", "Products"),
        ingredient: t("menu.products.ingredients", "Ingredients"),
        packaging: t("menu.products.packagings", "Packagings"),
        transport: t("transport.title", "Transport"),
    };

    const tabs = SCORES_DISPLAYED_ENTITY_TYPES.map((type) => ({
        id: type,
        label: tabLabels[type],
        count: getDisplayedScores(
            getScoreTypeSelection(scoreTypes.find((scoreType) => scoreType.type === type)).scoreIds,
            scores,
        ).length,
    }));

    return (
        <Box sx={{ padding: theme.spacing(4) }}>
            <Paper
                elevation={0}
                sx={{
                    border: `1px solid ${theme.custom.grey[500]}`,
                    borderRadius: theme.spacing(2),
                    boxShadow: theme.custom.shadows.grey,
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={theme.spacing(2)}
                    sx={{ padding: theme.spacing(4), borderBottom: `1px solid ${theme.custom.grey[500]}` }}
                >
                    <FoodsIconButton
                        icon="arrowLeftShort"
                        size="m"
                        buttonProps={{
                            onClick: navigateSettingsParent,
                            "aria-label": t("common.actions.back", "Back"),
                        }}
                    />
                    <Typography variant="h3">{t("scores_affiches", "Displayed scores")}</Typography>
                </Stack>

                <Stack gap={theme.spacing(3)} sx={{ padding: theme.spacing(3, 4, 4) }}>
                    <CountTabs
                        tabs={tabs}
                        activeId={activeType}
                        onChange={setActiveType}
                        ariaLabel={t("settings.scores.tabs.aria_label", "Entities")}
                    />
                    <Banner variant="info">
                        <Stack gap={theme.spacing(0.5)}>
                            <Typography variant="body-semibold">
                                {t(
                                    "settings.scores.eligibility_banner.title",
                                    "Only scores that apply to this entity are listed",
                                )}
                            </Typography>
                            <Typography variant="small-regular">
                                {t(
                                    "settings.scores.eligibility_banner.text",
                                    "Enabled scores are shown in this order. Drag to reorder, select a score to choose its variations and sub-scores.",
                                )}
                            </Typography>
                        </Stack>
                    </Banner>
                    <EntityScoresSettings
                        key={activeType}
                        type={activeType}
                        scores={scores}
                        canModify={canUser(MODIFY, SCORE_TYPE)}
                    />
                </Stack>
            </Paper>
        </Box>
    );
};
